'use client'
import React, { Suspense, useState } from 'react'
import {
  Box,
  Flex,
  Image,
  Tab,
  TabList,
  Tabs,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react'
import CombineInfo from './modal-section/combine-info'
import { useBillingCycleCreator } from '@/src/queries/earnings/new/billingCycleCreator'

type ShowAmountProps = {
  showAmount: boolean
}

type EarningsData = {
  label: string
  dollarAmount?: string
  lykcoinAmount?: string
  watchtimeInHours?: string
  viewsCount?: string
  subscribeCount?: string
  percentageChange: number
}

const EarningDisplay = ({
  showAmount,
  data,
}: {
  showAmount: boolean
  data: EarningsData
}) => {
  return (
    <Flex className="my-3 items-end justify-between">
      <Text className="text-4xl font-medium">
        {showAmount ? data.lykcoinAmount : data.dollarAmount}
      </Text>

      {data.watchtimeInHours ? (
        <Text>{`${data.watchtimeInHours} hrs`}</Text>
      ) : data.viewsCount ? (
        <Text>{data.viewsCount}</Text>
      ) : data.subscribeCount ? (
        <Text>{data.subscribeCount} </Text>
      ) : null}
    </Flex>
  )
}

const EarningTabsCreator = ({ showAmount }: ShowAmountProps) => {
  const [activeBox, setActiveBox] = useState(0)
  const [isCombineInfoOpen, setCombineInfoOpen] = useState(false)
  const [tabAllEarningIndex, setTabAllEarningIndex] = useState(0)
  const isSmallScreen = useBreakpointValue({ base: true, sm: false })
  const currentBillingCreator = useBillingCycleCreator()

  const handleCombineMoreInfoOpen = () => {
    setCombineInfoOpen(true)
  }

  const earningsData = [
    {
      label: 'Earnings',
      dollarAmount: `${currentBillingCreator.data?.earning?.currency || ''}${currentBillingCreator.data?.earning?.moneyValue || '0'}`,
      lykcoinAmount: currentBillingCreator.data?.earning?.lykCoin || '0',
      percentageChange: currentBillingCreator.data?.earning?.incValue || '0',
      incType: currentBillingCreator.data?.earning?.incType || 0,
    },
    {
      label: 'Watchtime',
      watchtimeInHours: (
        (currentBillingCreator.data?.watchtime?.watchtime || 0) / 3600
      ).toFixed(2),
      percentageChange: currentBillingCreator.data?.watchtime?.incValue || '0',
      incType: currentBillingCreator.data?.watchtime?.incType || 0,
    },
    {
      label: 'Views',
      viewsCount: currentBillingCreator.data?.views?.views || 0,
      percentageChange: currentBillingCreator.data?.views?.incValue || '0',
      incType: currentBillingCreator.data?.views?.incType || 0,
    },
    {
      label: 'Subscribers',
      subscribeCount: currentBillingCreator.data?.subscriber?.subscribers || 0,
      percentageChange: currentBillingCreator.data?.subscriber?.incValue || '0',
      incType: currentBillingCreator.data?.subscriber?.incType || 0,
    },
  ]

  interface GetInfoMessageProps {
    incValue: string
    incType: number
    tabAllEarningIndex: number
    index: number
    isSmallScreen: boolean
    activeBox: number
  }

  const getInfoMessage = ({
    incValue,
    incType,
  }: GetInfoMessageProps): JSX.Element | string => {
    const percentageNumber = parseFloat(incValue.replace('%', ''))

    switch (incType) {
      case 1: // 1 - increase
        return (
          <div className="flex items-center gap-1">
            <Image
              src={
                isSmallScreen
                  ? '/rewards/breakdown/lineUpLight.svg'
                  : '/rewards/breakdown/lineUpDark.svg'
              }
              alt="Increase Icon"
              w={4}
              h={4}
            />
            {` ${percentageNumber}% higher than last month`}
          </div>
        )
      case 0: // 0 - decrease
        return (
          <div className="flex items-center gap-1">
            <Image
              src={
                isSmallScreen
                  ? '/rewards/breakdown/lineDownLight.svg'
                  : '/rewards/breakdown/lineDownDark.svg'
              }
              alt="Decrease Icon"
              w={4}
              h={4}
            />
            {` ${Math.abs(percentageNumber)}% lower than last month`}
          </div>
        )
      case 2: // 2 - similar
        return (
          <div className="flex items-center gap-1">
            <Image
              src={
                isSmallScreen
                  ? '/rewards/breakdown/neutralLineLight.svg'
                  : '/rewards/breakdown/neutralLineDark.svg'
              }
              alt="Neutral Icon"
              w={4}
              h={4}
            />
            {` Similar to last month`}
          </div>
        )
      default:
        return 'Not valid'
    }
  }

  // Tabs for mobile view
  const tabsEarning = [
    { label: 'Earnings' },
    { label: 'Watchtime' },
    { label: 'Views' },
    { label: 'Subscribers' },
  ]

  return (
    <Suspense fallback={'Loading'}>
      <CombineInfo
        isOpen={isCombineInfoOpen}
        onClose={() => setCombineInfoOpen(false)}
      />

      {/* For mobile view */}
      <Tabs
        index={tabAllEarningIndex}
        onChange={(index) => setTabAllEarningIndex(index)}
        variant="soft-rounded"
        colorScheme="green"
        size="sm"
        display={{ base: 'block', sm: 'none' }}
      >
        <TabList
          justifyContent={'space-between'}
          className="mb-4 mt-6 rounded-full border-2 border-[#D3D3D3] p-0.5"
        >
          {tabsEarning.map((tab, index) => (
            <Tab
              key={index}
              bg={tabAllEarningIndex === index ? '#24BFA3' : '#FFFFFF'}
              color={tabAllEarningIndex === index ? '#FFFFFF' : '#9F9F9F'}
              _selected={{ bg: '#24BFA3', color: '#FFFFFF' }}
              px={2}
            >
              {tab.label}
            </Tab>
          ))}
        </TabList>
      </Tabs>

      {/* For mobile view */}
      <Flex
        display={{ base: 'flex', sm: 'none' }}
        className="flex-col gap-3 sm:flex-row sm:justify-between"
      >
        {earningsData.map((data, index) => (
          <Box
            key={index}
            onClick={() => {
              if (!isSmallScreen) setActiveBox(index)
            }}
            className={`w-full rounded-xl border-2 border-[#D3D3D3] p-3 ${
              isSmallScreen
                ? index === tabAllEarningIndex
                  ? 'bg-[#24BFA3] text-white'
                  : 'bg-white text-[#656565]'
                : activeBox === index
                  ? 'bg-[#24BFA3] text-white'
                  : 'bg-white text-[#656565]'
            } ${isSmallScreen && index !== tabAllEarningIndex ? 'hidden sm:block' : ''}`}
            cursor="pointer"
          >
            <Flex direction="column">
              <Flex className="items-center justify-between">
                <Text className="text-lg font-medium">{data.label}</Text>
                <Image
                  src={
                    (isSmallScreen && index === tabAllEarningIndex) ||
                    (!isSmallScreen && activeBox === index)
                      ? '/rewards/breakdown/exclamatoryWhite.svg'
                      : '/rewards/breakdown/exclamatoryGray.svg'
                  }
                  alt="Icon"
                  w={4}
                  h={4}
                  onClick={handleCombineMoreInfoOpen}
                />
              </Flex>

              <Flex className="my-3 items-end justify-between">
                <Text className="text-4xl font-medium">
                  <EarningDisplay showAmount={showAmount} data={data} />
                </Text>
              </Flex>
              <Text className="text-sm">
                {getInfoMessage({
                  incValue: data.percentageChange,
                  incType: data.incType,
                  tabAllEarningIndex,
                  index,
                  isSmallScreen: isSmallScreen ?? false,
                  activeBox,
                })}
              </Text>
            </Flex>
          </Box>
        ))}
      </Flex>

      {/* For desktop view */}
      <Flex
        display={{ base: 'none', sm: 'flex' }}
        className="hidden flex-col gap-3  rounded-xl border-2 border-[#ECECEC] p-4 sm:flex sm:flex-row sm:justify-between"
        bg="linear-gradient(52.62deg, rgba(255, 255, 255, 0.2) 12.76%, rgba(36, 191, 163, 0.2) 110.86%)"
      >
        {earningsData.map((data, index) => (
          <Box key={index} className={`w-full`}>
            <Flex direction="column">
              <Flex className="items-center gap-2">
                <Image
                  src={'/rewards/breakdown/exclamatoryGray.svg'}
                  alt="Icon"
                  w={4}
                  h={4}
                  onClick={handleCombineMoreInfoOpen}
                  cursor="pointer"
                />
                <Text className="text-lg font-semibold text-[#656565]">
                  {data.label}
                </Text>
              </Flex>

              <Flex className="my-3 items-end justify-between">
                <Text className="text-4xl font-medium text-[#797979]">
                  <EarningDisplay showAmount={showAmount} data={data} />
                </Text>
              </Flex>
              <Text className="text-sm font-semibold text-[#9F9F9F]">
                {getInfoMessage({
                  incValue: data.percentageChange,
                  incType: data.incType,
                  tabAllEarningIndex,
                  index,
                  isSmallScreen: isSmallScreen ?? false,
                  activeBox,
                })}
              </Text>
            </Flex>
          </Box>
        ))}
      </Flex>
    </Suspense>
  )
}

export default EarningTabsCreator
